
import {Container, Graphics, HTMLText, HTMLTextStyle, Sprite, Text, TextStyle, Texture} from "pixi.js";
import { DRect } from "src/app/binst/primitives/drawable-rect";
import {PixiObject} from "../primitives/pixi-object";
import {BlueprintContext} from "../../../world/cyanprints/i-blueprint-context";
import {Rect, RectConstructor} from "../../../primitives/rect";


export function bodyStyle(width: number, fontSize: number): TextStyle {
  const boldFontSize = fontSize + 4;
  const textStyle = new HTMLTextStyle({
    fontSize: fontSize,
    fontWeight: "normal",
    fill: "#ffffff",
    wordWrap: true,
    wordWrapWidth: width,
    breakWords: false,
    align: "left",
    padding: 5,
    cssOverrides: [
        `p { 
            line-height: 1.1; 
            margin:0; 
            padding: 0px; 
            font-weight: 100; 
            font-family: 'lineal';
            /* Prevents text from overflowing by breaking long words if necessary */
            overflow-wrap: break-word; 
            /* Fallback/standard alias */
            word-break: break-word;
            width: ${width}px!important;
        }`,
        `strong {
            line-height: 1.1; 
            margin:0; 
            padding:0; 
            font-weight: bold; 
            font-family: 'lineal';
        }`,
        `code {
            font-family: monospace;
            color: coral;
            background: #1d1d1d;
            border-radius: 10px;
            padding-left: 5px;
            padding-right: 5px;
         }`,
        `blockquote {
            line-height: 1.2; 
            margin: 0;
            padding-left: 16px;
            margin-bottom: 10px;
            border-left: 3px solid #bcbcbc;
            margin-left: 0;
            margin-right: 0;
        }`,
    ]
  });
  return textStyle;
}

export function fontSizeForLength(input: string): number {
    const len = input.length;
    if (len <= 20) return 45;   // Ultra-short (e.g., "Hello!")
    if (len <= 50) return 40;   // Short phrases
    if (len <= 100) return 38;  // 1-2 sentences
    if (len <= 180) return 34;  // Small paragraph
    if (len <= 300) return 30;  // Standard paragraph
    if (len <= 500) return 28;  // Long-form story
    if (len <= 800) return 26;  // Extra long
    return 14;                  // Deep dive/Technical
}

type TextBoxDrawableConfig = ConstructorParameters<typeof Sprite>[0] & {
    text: string;
    width: number;
    height: number;
    blueprintContext: BlueprintContext;
}

export class TextBoxDrawable extends PixiObject {
    blueprintContext: BlueprintContext;
    override config: TextBoxDrawableConfig
    pixiText: HTMLText;

    constructor(config: TextBoxDrawableConfig) {
        if (typeof config.text !== "string") {
            console.log("Text needs to be a string: ", config.text);
            throw new Error("Text needs to be a string: " + config.text);
        }
        super({id: "textBox-" + config.text.slice(0, 10)});
        config.text = this.textMod(config.text);
        this.blueprintContext = config.blueprintContext;
        this.config = config;
        this.build();
    }

    textMod(text: string) {
        return  `<div style="margin-right: 10px; margin-bottom:10px;">${text}</div>`
    }

    updateText(text: string) {
        this.config.text = this.textMod(text);
        this.build();
    }

    private build() {
        if (this.pixiText) {
            this.sprite.removeChild(this.pixiText);
            this.pixiText.destroy();
        }
        const fontSize = fontSizeForLength(this.config.text)
        this.pixiText = new HTMLText({
            text: this.config.text,
            style: bodyStyle(this.config.width, fontSize)
        });
        // FIXME: Bounds is unreliable as it changes when we scale in/out with surfaces
        const size = this.pixiText.getBounds()
        this.sprite.width = this.pixiText.width;
        this.sprite.height = this.pixiText.height;
        this.sprite.addChild(this.pixiText);
        this.updateTouchBounds()
    }

    override setupDefaultInteractions() {
        this.touchPrimitive.onPressed((pt) => {
            this.pixiText.alpha = 0.5;
            this.requestRender()
            return true; // Stop propagation
        });
        this.touchPrimitive.onReleased((pt) => {
            this.pixiText.alpha = 1;
            this.requestRender()
            return true; // Stop propagation
        });
    }

    addDebugRect(width, height) {
        const graphics = new Graphics({});
        const rect = new DRect({
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            roundness: 5,
            colour: "rgba(58,52,49,0.53)",
        });
        rect.render(graphics);
        this.sprite.addChild(graphics);
    }
}

